import { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Tag } from 'lucide-react';
import Header from "./Header";
import LoadingSpinner from "./LoadingSpinner";
import {
  fetchItems, fetchArchivedItems, fetchNewItems,
  fetchSpecialOfferItems, fetchMuseumItems,
  fetchCategories, fetchArchivedCategories
} from "../api";

const STATIC_TITLES = {
  new: 'New',
  'special-offers': 'Special Offers',
  museum: 'Museum',
};

// mode: 'category' | 'archived' | 'new' | 'special-offers' | 'museum'
export default function ItemsPage({ mode, onBack }) {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  // Fast path: CategoriesPage passes the title via navigate state, so we skip an extra fetch.
  const [title, setTitle] = useState(
    STATIC_TITLES[mode] || location.state?.categoryTitle || null
  );

  // Resolve title for category/archived modes if it wasn't handed to us
  // (e.g. someone opened /categories/42 directly from a bookmark or shared link).
  useEffect(() => {
    if (title || (mode !== 'category' && mode !== 'archived')) return;

    let cancelled = false;
    const resolveTitle = async () => {
      const fetcher = mode === 'archived' ? fetchArchivedCategories : fetchCategories;
      const categories = await fetcher();
      const match = categories.find((c) => String(c.id) === String(categoryId));
      if (!cancelled && match) setTitle(match.title);
    };
    resolveTitle();
    return () => { cancelled = true; };
  }, [mode, categoryId, title]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      let data;
      switch (mode) {
        case 'category':
          data = (await fetchItems(categoryId)).sort((a, b) => a.price - b.price);
          break;
        case 'archived':
          data = (await fetchArchivedItems(categoryId)).sort((a, b) => a.price - b.price);
          break;
        case 'new':
          data = (await fetchNewItems()).sort((a, b) => a.price - b.price);
          break;
        case 'special-offers':
          data = (await fetchSpecialOfferItems()).sort((a, b) => a.specialOfferPrice - b.specialOfferPrice);
          break;
        case 'museum':
          data = await fetchMuseumItems();
          break;
        default:
          data = [];
      }
      if (!cancelled) {
        setItems(data);
        setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [mode, categoryId]);

  const handleItemSelect = (itemId) => {
    navigate(mode === 'museum' ? `/museum/${itemId}` : `/items/${itemId}`);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-800">
      <Header title={title} onBack={onBack} />

      <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
        {loading ? (
          <LoadingSpinner />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.length === 0 ? (
              <div className="col-span-full text-center py-8 text-gray-500">
                No items found
              </div>
            ) : (
              items.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleItemSelect(item.id)}
                  className="bg-white dark:bg-gray-700 rounded-lg shadow-sm border border-gray-200 dark:border-gray-500 hover:shadow-md dark:hover:shadow-gray-700 transition-all duration-200 overflow-hidden text-left relative md:hover:scale-[1.02]"
                >
                  {item.reserved && (
                    <div className="absolute top-3 -left-10 bg-indigo-600 dark:bg-indigo-400 text-white px-12 py-1 text-xs font-bold rotate-[-35deg] shadow-lg z-10">
                      RESERVED
                    </div>
                  )}
                  {!item.reserved && item.specialOffer && (
                    <div className="absolute top-3 -left-10 bg-pink-700 text-white px-12 py-1 text-xs font-bold rotate-[-35deg] shadow-lg z-10">
                      SALE
                    </div>
                  )}
                  {/* Mobile: Horizontal list item */}
                  <div className="flex items-stretch md:hidden">
                    {item.photoUrl && (
                      <div className="relative w-24">
                        <img
                          src={item.photoUrl}
                          alt={item.title}
                          className="w-full h-full object-cover"
                        />
                        {item.reserved && (
                          <div className="absolute inset-0 bg-black/50" />
                        )}
                      </div>
                    )}
                    {!item.photoUrl && (
                      <Tag className="w-24 h-24 text-gray-300 p-4" />
                    )}
                    <div className="flex-1 p-4">
                      <h3 className="font-semibold text-gray-800 dark:text-white mb-1">
                        {item.title}
                      </h3>
                      {item.specialOffer && (
                        <div className="text-lg font-bold">
                          <span className="text-pink-700 dark:text-pink-600">
                            {item.specialOfferPrice.toFixed(0)} RSD
                          </span>
                          <span className="line-through ml-3 text-indigo-600 dark:text-indigo-200">
                            {item.price.toFixed(0)} RSD
                          </span>
                        </div>
                      )}
                      {item.price && !item.specialOffer && !item.archived && (
                        <div className="text-lg font-bold text-indigo-600 dark:text-indigo-200">
                          {item.price.toFixed(0)} RSD
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Desktop: Vertical card */}
                  <div className="hidden md:flex flex-col">
                    {item.photoUrl && (
                      <div className="relative w-full h-48">
                        <img
                          src={item.photoUrl}
                          alt={item.title}
                          className="w-full h-full object-cover"
                        />
                        {item.reserved && (
                          <div className="absolute inset-0 bg-black/50" />
                        )}
                      </div>
                    )}
                    {!item.photoUrl && (
                      <div className="w-full h-48 flex items-center justify-center bg-gray-100 dark:bg-gray-600">
                        <Tag className="w-16 h-16 text-gray-300" />
                      </div>
                    )}
                    <div className="p-4">
                      <h3 className="font-semibold text-gray-800 dark:text-white mb-2">
                        {item.title}
                      </h3>
                      {item.specialOffer && (
                        <div className="text-lg font-bold">
                          <span className="text-pink-700 dark:text-pink-600">
                            {item.specialOfferPrice.toFixed(0)} RSD
                          </span>
                          <span className="line-through ml-3 text-indigo-600 dark:text-indigo-200">
                            {item.price.toFixed(0)} RSD
                          </span>
                        </div>
                      )}
                      {item.price && !item.specialOffer && !item.archived && (
                        <div className="text-lg font-bold text-indigo-600 dark:text-indigo-200">
                          {item.price.toFixed(0)} RSD
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}